import { TARGET_SAMPLE_RATE } from '@renderer/services/audio-transcription/types'

const SAMPLES_PER_MS = TARGET_SAMPLE_RATE / 1000

/**
 * The last stretch of mic audio, addressable by wall-clock time, so a final
 * transcript can be matched back to the audio it was heard in. Times are when
 * each frame reached the pipeline, which is close enough for utterances that
 * last seconds.
 */
export class MicAudioHistory {
  private readonly samples: Float32Array
  /** Samples appended since the start, including those already overwritten. */
  private written = 0
  private lastAppendAtMs = 0

  constructor(seconds: number) {
    this.samples = new Float32Array(seconds * TARGET_SAMPLE_RATE)
  }

  /** Appends a PCM16 frame at the pipeline's rate. */
  append(pcm16: ArrayBuffer, atMs: number): void {
    const frame = new Int16Array(pcm16)
    for (let i = 0; i < frame.length; i++) {
      this.samples[(this.written + i) % this.samples.length] = frame[i] / 0x8000
    }
    this.written += frame.length
    this.lastAppendAtMs = atMs
  }

  /** The audio heard between two times, or null when any of it has rolled out of the history or not arrived yet. */
  slice(startMs: number, endMs: number): Float32Array | null {
    const toIndex = (ms: number) => Math.round(this.written - (this.lastAppendAtMs - ms) * SAMPLES_PER_MS)
    const start = toIndex(startMs)
    const end = toIndex(endMs)
    if (end <= start || start < Math.max(0, this.written - this.samples.length) || end > this.written) return null
    const out = new Float32Array(end - start)
    for (let i = 0; i < out.length; i++) out[i] = this.samples[(start + i) % this.samples.length]
    return out
  }
}
